import React, { useState, useEffect } from "react";
import axios from "axios";
import { useMutation, useQueryClient, UseMutationResult } from "react-query";
import TTax from "../types/TTax";
import { AxiosResponse } from "axios";

type TaxModalProps = {
  taxes: TTax[];
  isTaxModalOpen: boolean;
  setIsTaxModalOpen: (isOpen: boolean) => void;
  selectedTax: TTax | null;
  setSelectedTax: (tax: TTax | null) => void;
};

const ToggleSwitch = ({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: () => void;
}) => (
  <label className="inline-flex items-center cursor-pointer">
    <input
      type="checkbox"
      className="sr-only peer"
      checked={checked}
      onChange={onChange}
    />
    <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-500"></div>
    <span className="ml-3 text-sm font-medium text-gray-700">
      Same Rate for Sale & Install
    </span>
  </label>
);

const TaxModal = ({
  taxes,
  isTaxModalOpen,
  setIsTaxModalOpen,
  selectedTax,
  setSelectedTax,
}: TaxModalProps) => {
  const queryClient = useQueryClient();

  const formatInputValue = (value: number | string) => {
    if (typeof value === "number") value = value.toString();
    return value.includes(".")
      ? value
      : parseFloat(value).toFixed(2).toString();
  };

  const defaultFormData: TTax = {
    id: "",
    label: "",
    list_rate: 0,
    list_type: "percent",
    sale_rate: 0,
    sale_type: "percent",
    install_rate: 0,
    install_type: "percent",
    tax_type: "tax",
    archived: false,
  };

  const [formData, setFormData] = useState<TTax>(defaultFormData);
  const [isSameRate, setIsSameRate] = useState(true);

  useEffect(() => {
    if (selectedTax) {
      const foundTax =
        taxes.find((tax) => tax.id === selectedTax.id) || selectedTax;
      setFormData({
        ...foundTax,
        list_rate:
          foundTax.list_type === "fixed"
            ? foundTax.list_rate / 100
            : foundTax.list_rate,
        archived: foundTax.archived,
      });
      setIsSameRate(foundTax.sale_rate === foundTax.install_rate);
    } else {
      setFormData(defaultFormData);
      setIsSameRate(true);
    }
  }, [selectedTax, taxes]);

  useEffect(() => {
    if (selectedTax) {
      const updatedTax = taxes.find((tax) => tax.id === selectedTax.id);
      if (updatedTax) {
        setSelectedTax(updatedTax);
      }
    }
  }, [taxes]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name.includes("rate") ? value : value,
    }));
  };

  const handleToggleChange = () => {
    setIsSameRate((prev) => !prev);
  };

  // ✅ Mutation for updating/creating tax
  interface TaxMutationData {
    id: string;
    label: string;
    list_rate: number;
    list_type: "fixed" | "percent";
    sale_rate: number;
    sale_type: "fixed" | "percent";
    install_rate: number;
    install_type: "fixed" | "percent";
    tax_type: "tax" | "fee" | "discount";
  }

  interface TaxMutationError {
    message: string;
  }

  const taxMutation: UseMutationResult<
    AxiosResponse,
    TaxMutationError,
    TaxMutationData
  > = useMutation({
    mutationFn: async (data: TaxMutationData) => {
      if (data.id) {
        return axios.put(
          `${process.env.REACT_APP_SERVER_URL}/api/v1/company/tax/update`,
          data
        );
      } else {
        return axios.post(
          `${process.env.REACT_APP_SERVER_URL}/api/v1/company/tax/new`,
          data
        );
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["taxes"]);
      setIsTaxModalOpen(false);
      setSelectedTax(null);
    },
    onError: (error: TaxMutationError) => {
      console.error("Failed to save tax:", error);
      alert("Failed to save tax. Please try again.");
    },
  });

  const handleSave = () => {
    if (!formData.label.trim()) {
      alert("Please enter a valid tax name.");
      return;
    }

    const isDiscount = formData.tax_type === "discount";
    const isFixed = formData.list_type === "fixed";

    const formattedData = {
      ...formData,
      list_rate: isFixed
        ? Math.round(Math.abs(Number(formData.list_rate)) * 100) *
          (isDiscount ? -1 : 1)
        : Math.abs(formData.list_rate) * (isDiscount ? -1 : 1),
      sale_rate: isSameRate
        ? isFixed
          ? Math.round(Math.abs(Number(formData.list_rate)) * 100) *
            (isDiscount ? -1 : 1)
          : Math.abs(formData.list_rate) * (isDiscount ? -1 : 1)
        : Math.abs(Number(formData.sale_rate) * 100) * (isDiscount ? -1 : 1),
      install_rate: isSameRate
        ? isFixed
          ? Math.round(Math.abs(Number(formData.list_rate)) * 100) *
            (isDiscount ? -1 : 1)
          : Math.abs(formData.list_rate) * (isDiscount ? -1 : 1)
        : Math.abs(Number(formData.install_rate) * 100) * (isDiscount ? -1 : 1),
      sale_type: formData.list_type,
      install_type: formData.list_type,
    };

    taxMutation.mutate(formattedData);
  };

  // 🆕 Sample Calculation
  const baseAmount = 100;
  const rate = Math.abs(formData.list_rate);
  const isPercentage = formData.list_type === "percent";

  const archiveTax = useMutation(
    async (tax: { id: number; archived: boolean }) => {
      const response = await axios.put(
        `${process.env.REACT_APP_SERVER_URL}/api/v1/company/tax/archive/${tax.id}`,
        { archived: !tax.archived }
      );

      return { id: tax.id, archived: !tax.archived };
    },
    {
      onMutate: async (updatedTax) => {
        await queryClient.cancelQueries("taxes");

        // Snapshot current data
        const previousTaxes = queryClient.getQueryData<TTax[]>("taxes");

        // **Ensure previousTaxes is an array**
        if (!Array.isArray(previousTaxes)) {
          console.warn(
            "previousTaxes is not an array. Skipping optimistic update."
          );
          return { previousTaxes };
        }

        // Update the UI optimistically
        queryClient.setQueryData<TTax[]>("taxes", (oldTaxes = []) =>
          oldTaxes.map((tax) =>
            String(tax.id) === String(updatedTax.id)
              ? { ...tax, archived: !updatedTax.archived }
              : tax
          )
        );

        return { previousTaxes };
      },
      onError: (err, newTax, context) => {
        console.error("Error archiving tax:", err);
        if (context?.previousTaxes) {
          queryClient.setQueryData("taxes", context.previousTaxes);
        }
      },
      onSettled: () => {
        queryClient.invalidateQueries("taxes");
      },
    }
  );

  if (!isTaxModalOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md shadow-lg">
        <h3 className="text-lg font-semibold mb-4">
          {selectedTax ? "Edit Tax" : "Create New Tax"}
        </h3>

        <div className="mb-4 overflow-y-auto max-h-96 p-1">
          {/* Label */}
          <label className="block mb-2">
            <span className="text-gray-700 font-medium">Label</span>
            <input
              type="text"
              name="label"
              value={formData.label}
              onChange={handleChange}
              placeholder="Tax Name"
              className="w-full p-2 border border-gray-300 rounded"
            />
          </label>

          {/* Tax Type */}
          <label className="block mb-2">
            <span className="text-gray-700 font-medium">Tax Type</span>
            <select
              name="tax_type"
              value={formData.tax_type}
              onChange={handleChange}
              className="w-full p-2 border border-gray-300 rounded"
            >
              <option value="tax">Tax</option>
              <option value="fee">Fee</option>
              <option value="discount">Discount</option>
            </select>
          </label>

          {/* Rate Type */}
          <label className="block mb-2">
            <span className="text-gray-700 font-medium">Rate Type</span>
            <select
              name="list_type"
              value={formData.list_type}
              onChange={handleChange}
              className="w-full p-2 border border-gray-300 rounded"
            >
              <option value="percent">Percentage</option>
              {formData.tax_type !== "tax" && (<option value="fixed">Fixed</option>)}
            </select>
          </label>

          {/* Orange Toggle Switch */}
          <div className="mt-4 mb-2">
            <ToggleSwitch checked={isSameRate} onChange={handleToggleChange} />
            <div className="text-xs text-gray-500 mt-1">
              Fixed values are in decimal dollars (e.g., 1.50 = $1.50).
            </div>
          </div>

          {/* Single List Rate Input */}
          {isSameRate && (
            <label className="block mb-2">
              <span className="text-gray-700 font-medium">Rate</span>
              <input
                type="number"
                name="list_rate"
                step="0.01"
                value={formatInputValue(formData.list_rate)}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded"
              />
            </label>
          )}

          {/* Sale & Install Rates */}
          {!isSameRate && (
            <>
              <label className="block mb-2">
                <span className="text-gray-700 font-medium">Sale Rate</span>
                <input
                  type="number"
                  name="sale_rate"
                  step="0.01"
                  value={formatInputValue(formData.sale_rate)}
                  onChange={handleChange}
                  className="w-full p-2 border border-gray-300 rounded"
                />
              </label>
              <label className="block mb-2">
                <span className="text-gray-700 font-medium">Install Rate</span>
                <input
                  type="number"
                  name="install_rate"
                  step="0.01"
                  value={formatInputValue(formData.install_rate)}
                  onChange={handleChange}
                  className="w-full p-2 border border-gray-300 rounded"
                />
              </label>
            </>
          )}

          {/* Sample */}
          <div className="mt-4 p-4 bg-gray-100 rounded border border-gray-300">
            <span className="block text-gray-700 font-medium mb-2">
              Sample Calculation:
            </span>

            {isSameRate ? (
              <>
                {/* Labels Row */}
                <div className="grid grid-cols-3 text-xs text-gray-500 font-semibold border-b pb-1">
                  <span className="text-left">Base Amount</span>
                  <span className="text-center">Your Modifier</span>
                  <span className="text-right">Result</span>
                </div>

                {/* Values Row */}
                <div className="grid grid-cols-3 text-lg font-medium pt-2">
                  <span className="text-gray-800 text-left">
                    ${baseAmount.toFixed(2)}
                  </span>
                  <span className="text-gray-800 text-center">
                    {formData.tax_type === "discount" ? "-" : "+"}
                    {isPercentage
                      ? `${parseFloat(
                          Math.abs(formData.list_rate).toString()
                        ).toFixed(2)}%`
                      : `$${parseFloat(
                          Math.abs(formData.list_rate).toString()
                        ).toFixed(2)}`}
                  </span>
                  <span
                    className={`text-right font-semibold ${
                      formData.tax_type === "discount"
                        ? "text-red-500"
                        : "text-green-600"
                    }`}
                  >
                    $
                    {(formData.tax_type === "discount"
                      ? baseAmount -
                        (isPercentage
                          ? (parseFloat(formData.list_rate.toString()) / 100) *
                            baseAmount
                          : parseFloat(formData.list_rate.toString()))
                      : baseAmount +
                        (isPercentage
                          ? (parseFloat(formData.list_rate.toString()) / 100) *
                            baseAmount
                          : parseFloat(formData.list_rate.toString()))
                    ).toFixed(2)}
                  </span>
                </div>
              </>
            ) : (
              <>
                {/* SALE RATE */}
                <div className="grid grid-cols-3 text-xs text-gray-500 font-semibold border-b pb-1 mt-3">
                  <span className="text-left">Base Amount</span>
                  <span className="text-center">Sale Modifier</span>
                  <span className="text-right">Sale Result</span>
                </div>

                <div className="grid grid-cols-3 text-lg font-medium pt-2">
                  <span className="text-gray-800 text-left">
                    ${baseAmount.toFixed(2)}
                  </span>
                  <span className="text-gray-800 text-center">
                    {formData.tax_type === "discount" ? "-" : "+"}
                    {isPercentage
                      ? `${parseFloat(formData.sale_rate.toString()).toFixed(
                          2
                        )}%`
                      : `$${parseFloat(formData.sale_rate.toString()).toFixed(
                          2
                        )}`}
                  </span>
                  <span
                    className={`text-right font-semibold ${
                      formData.tax_type === "discount"
                        ? "text-red-500"
                        : "text-green-600"
                    }`}
                  >
                    $
                    {(formData.tax_type === "discount"
                      ? baseAmount -
                        (isPercentage
                          ? (parseFloat(formData.sale_rate.toString()) / 100) *
                            baseAmount
                          : parseFloat(formData.sale_rate.toString()))
                      : baseAmount +
                        (isPercentage
                          ? (parseFloat(formData.sale_rate.toString()) / 100) *
                            baseAmount
                          : parseFloat(formData.sale_rate.toString()))
                    ).toFixed(2)}
                  </span>
                </div>

                {/* INSTALL RATE */}
                <div className="grid grid-cols-3 text-xs text-gray-500 font-semibold border-b pb-1 mt-3">
                  <span className="text-left">Base Amount</span>
                  <span className="text-center">Install Modifier</span>
                  <span className="text-right">Install Result</span>
                </div>

                <div className="grid grid-cols-3 text-lg font-medium pt-2">
                  <span className="text-gray-800 text-left">
                    ${baseAmount.toFixed(2)}
                  </span>
                  <span className="text-gray-800 text-center">
                    {formData.tax_type === "discount" ? "-" : "+"}
                    {isPercentage
                      ? `${parseFloat(formData.install_rate.toString()).toFixed(
                          2
                        )}%`
                      : `$${parseFloat(
                          formData.install_rate.toString()
                        ).toFixed(2)}`}
                  </span>
                  <span
                    className={`text-right font-semibold ${
                      formData.tax_type === "discount"
                        ? "text-red-500"
                        : "text-green-600"
                    }`}
                  >
                    $
                    {(formData.tax_type === "discount"
                      ? baseAmount -
                        (isPercentage
                          ? (parseFloat(formData.install_rate.toString()) /
                              100) *
                            baseAmount
                          : parseFloat(formData.install_rate.toString()))
                      : baseAmount +
                        (isPercentage
                          ? (parseFloat(formData.install_rate.toString()) /
                              100) *
                            baseAmount
                          : parseFloat(formData.install_rate.toString()))
                    ).toFixed(2)}
                  </span>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Buttons */}
        <div className="flex justify-end space-x-4">
          <button
            onClick={() => {
              setIsTaxModalOpen(false);
              setSelectedTax(null);
            }}
            className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400 transition"
          >
            Cancel
          </button>
          {/* Archive Button - Only show if editing an existing tax */}
          {selectedTax && (
            <button
              onClick={() =>
                archiveTax.mutate({
                  id: Number(selectedTax.id!),
                  archived: selectedTax.archived,
                })
              }
              className={`px-4 py-2 rounded transition ${
                selectedTax.archived
                  ? "bg-green-500 hover:bg-green-600"
                  : "bg-red-500 hover:bg-red-600"
              } text-white`}
              disabled={archiveTax.isLoading}
            >
              {archiveTax.isLoading
                ? "Updating..."
                : selectedTax.archived
                ? "Unarchive"
                : "Archive"}
            </button>
          )}

          <button
            onClick={handleSave}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition"
            disabled={taxMutation.isLoading}
          >
            {taxMutation.isLoading
              ? "Saving..."
              : selectedTax
              ? "Update Tax"
              : "Create Tax"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TaxModal;
